import React from "react";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { Message } from "primereact/message";
import { Calendar } from "primereact/calendar";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { countries, Country } from "../../../data/countries";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Country as AppCountry } from "../../../models/MonetaryZones";

type RegisterStep1Props = {
  formik: any;
  appCountries: AppCountry[];
};

const RegisterStep1: React.FC<RegisterStep1Props> = ({
  formik,
  appCountries,
}) => {
  const genderOptions = [
    { label: "Female", value: "female" },
    { label: "Male", value: "male" },
  ];
  const selectedCountryTemplate = (option: Country, props: any) => {
    if (option) {
      return <div>{option.name}</div>;
    }

    return <span>{props.placeholder}</span>;
  };

  const countryOptionTemplate = (option: Country) => {
    return <div>{option.name}</div>;
  };

  const handleCountryChange = (event: DropdownChangeEvent) => {
    formik.setFieldValue("country", event.value.name);
  };

  const handleGenderChange = (event: DropdownChangeEvent) => {
    formik.setFieldValue("gender", event.value);
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
        {/* First Name */}
        <div className="field mb-4 col-span-2 md:col-span-1">
          <FloatLabel>
            <InputText
              id="firstname"
              name="firstname"
              autoComplete="given-name"
              value={formik.values.firstname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`w-full p-2 border rounded-md ${formik.errors.firstname && formik.touched.firstname ? "p-invalid border-red-500" : "border-gray-300"}`}
              placeholder=" "
            />
            <label htmlFor="firstname">First Name *</label>
          </FloatLabel>
          {formik.errors.firstname && formik.touched.firstname && (
            <Message
              severity="error"
              text={formik.errors.firstname}
              className="input-message"
            />
          )}
        </div>

        {/* Last Name */}
        <div className="field mb-4 col-span-2 md:col-span-1">
          <FloatLabel>
            <InputText
              id="lastname"
              name="lastname"
              autoComplete="family-name"
              value={formik.values.lastname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`w-full p-2 border rounded-md ${formik.errors.lastname && formik.touched.lastname ? "p-invalid border-red-500" : "border-gray-300"}`}
              placeholder=" "
            />
            <label htmlFor="lastname">Last Name *</label>
          </FloatLabel>
          {formik.errors.lastname && formik.touched.lastname && (
            <Message
              severity="error"
              text={formik.errors.lastname}
              className="input-message"
            />
          )}
        </div>

        {/* Email */}
        <div className="field mb-4  col-span-2">
          <FloatLabel>
            <InputText
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`w-full p-2 border rounded-md ${formik.errors.email && formik.touched.email ? "p-invalid border-red-500" : "border-gray-300"}`}
              placeholder=" "
            />
            <label htmlFor="email">Email *</label>
          </FloatLabel>
          {formik.errors.email && formik.touched.email && (
            <Message
              severity="error"
              text={formik.errors.email}
              className="input-message"
            />
          )}
        </div>

        {/* Phone Number */}
        <div className="field mb-4 col-span-2 md:col-span-1">
          {/* <FloatLabel> */}
          {/* <label htmlFor="phone">Phone Number *</label> */}
          <PhoneInput
            placeholder="Enter phone number"
            international
            id="phone"
            name="phone"
            autoComplete="tel"
            defaultCountry="CM"
            className={`w-full p-2 border rounded-md ${formik.errors.phone && formik.touched.phone ? "p-invalid border-red-500" : "border-gray-300"}`}
            value={formik.values.phone}
            onBlur={formik.handleBlur}
            onChange={(phone) => formik.setFieldValue("phone", phone)}
          />

          {formik.errors.phone && formik.touched.phone && (
            <Message
              severity="error"
              text={formik.errors.phone}
              className="input-message"
            />
          )}
        </div>

        {/* Country */}
        <div className="field mb-4 col-span-2 md:col-span-1">
          <FloatLabel>
            <Dropdown
              id="country"
              filter
              name="country"
              value={countries.find((c) => c.name === formik.values.country)}
              onBlur={formik.handleBlur}
              onChange={handleCountryChange}
              options={countries}
              optionLabel="name"
              placeholder="Select a Country"
              valueTemplate={selectedCountryTemplate}
              itemTemplate={countryOptionTemplate}
              emptyMessage="No countries found"
              filterPlaceholder="Search country..."
              className={`w-full border rounded-md ${formik.errors.country && formik.touched.country ? "p-invalid border-red-500" : "border-gray-300"}`}
            />
            <label htmlFor="country">Nationality *</label>
          </FloatLabel>
          {formik.errors.country && formik.touched.country && (
            <Message
              severity="error"
              text={formik.errors.country}
              className="input-message"
            />
          )}
        </div>
        <div className="field mb-4 col-span-2 md:col-span-1">
          <FloatLabel>
            <InputText
              id="city"
              name="city"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`w-full p-2 border rounded-md ${formik.errors.city && formik.touched.city ? "p-invalid border-red-500" : "border-gray-300"}`}
              placeholder=" "
            />
            <label htmlFor="city">City</label>
          </FloatLabel>
          {formik.errors.city && formik.touched.city && (
            <Message
              severity="error"
              text={formik.errors.city}
              className="input-message"
            />
          )}
        </div>

        {/* Address */}
        <div className="field mb-4 col-span-2 md:col-span-1">
          <FloatLabel>
            <InputText
              id="address"
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`w-full p-2 border rounded-md ${formik.errors.address && formik.touched.address ? "p-invalid border-red-500" : "border-gray-300"}`}
              placeholder=" "
            />
            <label htmlFor="address">Address</label>
          </FloatLabel>
          {formik.errors.address && formik.touched.address && (
            <Message
              severity="error"
              text={formik.errors.address}
              className="input-message"
            />
          )}
        </div>
        {!formik.values.isEntreprise && (
          <div className="field mb-4 col-span-2">
            <FloatLabel>
              <Dropdown
                id="countryId"
                filter
                name="countryId"
                value={appCountries.find(
                  (c) => c.id === formik.values.countryId
                )}
                onBlur={formik.handleBlur}
                onChange={(event: DropdownChangeEvent) =>
                  formik.setFieldValue("countryId", event.value.id)
                }
                options={appCountries}
                optionLabel="name"
                placeholder="Select a Country"
                valueTemplate={selectedCountryTemplate}
                itemTemplate={countryOptionTemplate}
                emptyMessage="No countries found"
                filterPlaceholder="Search country..."
                className={`w-full border rounded-md ${formik.errors.countryId && formik.touched.countryId ? "p-invalid border-red-500" : "border-gray-300"}`}
              />

              <label htmlFor="countryId">Country of Collection *</label>
            </FloatLabel>
            {formik.errors.countryId && formik.touched.countryId && (
              <Message
                severity="error"
                text={formik.errors.countryId}
                className="input-message"
              />
            )}
          </div>
        )}

        {/* Gender */}
        <div className="field mb-4 col-span-2 md:col-span-1">
          <FloatLabel>
            <Dropdown
              id="gender"
              name="gender"
              value={formik.values.gender}
              onBlur={formik.handleBlur}
              onChange={handleGenderChange}
              options={genderOptions}
              optionLabel="label"
              placeholder="Select a Gender"
              className={`w-full border rounded-md ${formik.errors.gender && formik.touched.gender ? "p-invalid border-red-500" : "border-gray-300"}`}
            />
            <label htmlFor="gender">Gender *</label>
          </FloatLabel>
          {formik.errors.gender && formik.touched.gender && (
            <Message
              severity="error"
              text={formik.errors.gender}
              className="input-message"
            />
          )}
        </div>

        {/* Date of Birth */}
        <div className="field mb-4 col-span-2 md:col-span-1">
          <FloatLabel>
            <Calendar
              showIcon
              id="dob"
              name="dob"
              value={formik.values.dob}
              onChange={formik.handleChange}
              dateFormat="dd/mm/yy"
              inputClassName="w-full p-2 rounded-md rounded-e-none"
              icon="pi pi-calendar"
              className={`w-full border rounded-md ${formik.errors.dob && formik.touched.dob ? "p-invalid border-red-500" : "border-gray-300"}`}
            />
            <label htmlFor="dob">Date of Birth</label>
          </FloatLabel>
          {formik.errors.dob && formik.touched.dob && (
            <Message
              severity="error"
              text={formik.errors.dob}
              className="input-message"
            />
          )}
        </div>
      </div>
    </form>
  );
};

export default RegisterStep1;
